import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Gauge, Warning, CheckCircle, Clock, CircleNotch, X, FloppyDisk, CaretDown, CaretRight, Drop } from '@phosphor-icons/react';
import axios from '../../utils/axiosInstance';
import { WlasnoscFilter, WlasnoscBadge, normalizeWlasnosc } from '../shared/WlasnoscFilter';

const API = `${window.location.origin}/api`;

const MONTHS_PL = [
    'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
    'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień',
];

function StatusPill({ warning, kmUntil }) {
    if (warning === 'no_history') {
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-600">
            Brak historii wymian
        </span>;
    }
    if (warning === 'overdue') {
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-rose-100 text-rose-700">
            <Warning className="w-3 h-3" weight="fill" /> Przeterminowana ({Math.abs(kmUntil)} km)
        </span>;
    }
    if (warning === 'soon') {
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800">
            <Warning className="w-3 h-3" weight="fill" /> Wkrótce ({kmUntil} km)
        </span>;
    }
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700">
        <CheckCircle className="w-3 h-3" weight="fill" /> OK (za {kmUntil} km)
    </span>;
}

export default function PrzebiegiTab({ pojazdy }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(null); // {pojazd_id, numer}
    const [form, setForm] = useState({ km: '', notes: '' });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);
    const [expandedId, setExpandedId] = useState(null);   // pojazd_id whose history is open
    const [historyCache, setHistoryCache] = useState({}); // pojazd_id → payload
    const [historyLoading, setHistoryLoading] = useState(false);
    const [wlasnoscFilter, setWlasnoscFilter] = useState('all');

    const filteredRows = React.useMemo(() => {
        if (wlasnoscFilter === 'all') return rows;
        return rows.filter((r) => normalizeWlasnosc(r.wlasnosc) === wlasnoscFilter);
    }, [rows, wlasnoscFilter]);

    const wlasnoscCounts = React.useMemo(() => {
        const c = { all: rows.length, firmowy: 0, prywatny: 0 };
        for (const r of rows) c[normalizeWlasnosc(r.wlasnosc)]++;
        return c;
    }, [rows]);

    const toggleHistory = useCallback(async (pojazdId) => {
        if (expandedId === pojazdId) { setExpandedId(null); return; }
        setExpandedId(pojazdId);
        if (historyCache[pojazdId]) return;
        setHistoryLoading(true);
        try {
            const { data } = await axios.get(`${API}/przebiegi/history/${pojazdId}`);
            setHistoryCache(prev => ({ ...prev, [pojazdId]: data }));
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setHistoryLoading(false);
        }
    }, [expandedId, historyCache]);

    const fetchStatus = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/przebiegi/status`);
            setRows(data);
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchStatus(); }, [fetchStatus]);

    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth() + 1;

    const openDialog = (row) => {
        setDialog({ pojazd_id: row.pojazd_id, numer: row.numer_rejestracyjny });
        setForm({ km: row.last_km ? String(Math.floor(row.last_km)) : '', notes: '' });
        setErr(null);
    };

    const submit = async () => {
        if (!form.km || Number(form.km) <= 0) {
            setErr('Wpisz aktualny przebieg (km)');
            return;
        }
        setSaving(true);
        setErr(null);
        try {
            await axios.post(`${API}/przebiegi`, {
                pojazd_id: dialog.pojazd_id,
                year: y, month: m,
                km: Number(form.km),
                notes: form.notes || null,
            });
            setDialog(null);
            await fetchStatus();
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="text-center py-12"><CircleNotch className="w-8 h-8 animate-spin mx-auto text-muted-foreground" /></div>;
    }

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div>
                    <h3 className="font-heading text-lg font-bold flex items-center gap-2">
                        <Gauge className="w-5 h-5 text-blue-600" weight="bold" /> Przebiegi pojazdów
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">
                        Wpisuj przebieg 1-go dnia każdego miesiąca. System ostrzeże o zbliżającej się wymianie oleju (10 000 km).
                    </p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <WlasnoscFilter value={wlasnoscFilter} onChange={setWlasnoscFilter} counts={wlasnoscCounts} />
                    <div className="text-xs text-slate-500">Okres: <span className="font-semibold text-slate-800">{MONTHS_PL[m - 1]} {y}</span></div>
                </div>
            </div>

            {filteredRows.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground text-sm">
                    {rows.length === 0 ? 'Brak pojazdów w systemie' : 'Brak pojazdów w wybranym filtrze'}
                </div>
            ) : (
                <div className="overflow-x-auto border border-slate-200 rounded">
                    <table className="w-full text-sm min-w-[720px]">
                        <thead className="bg-slate-50">
                            <tr>
                                <th className="text-left py-2 px-3 font-semibold text-slate-600 text-xs uppercase">Pojazd</th>
                                <th className="text-right py-2 px-3 font-semibold text-slate-600 text-xs uppercase">Ostatni przebieg</th>
                                <th className="text-right py-2 px-3 font-semibold text-slate-600 text-xs uppercase">Ostatnia wymiana</th>
                                <th className="text-right py-2 px-3 font-semibold text-slate-600 text-xs uppercase">Od wymiany</th>
                                <th className="text-center py-2 px-3 font-semibold text-slate-600 text-xs uppercase">Status</th>
                                <th className="text-center py-2 px-3 font-semibold text-slate-600 text-xs uppercase">Ten miesiąc</th>
                                <th className="text-right py-2 px-3 font-semibold text-slate-600 text-xs uppercase">Akcja</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredRows.map((r) => {
                                const p = pojazdy.find(x => x.id === r.pojazd_id) || {};
                                const isOpen = expandedId === r.pojazd_id;
                                const hist = historyCache[r.pojazd_id];
                                return (
                                    <React.Fragment key={r.pojazd_id}>
                                    <tr className={`border-t border-slate-100 hover:bg-slate-50/60 ${isOpen ? "bg-blue-50/40" : ""}`} data-testid={`przebieg-row-${r.pojazd_id}`}>
                                        <td className="py-2 px-3">
                                            <button
                                                onClick={() => toggleHistory(r.pojazd_id)}
                                                className="inline-flex items-center gap-1 text-left w-full"
                                                title="Pokaż historię"
                                                data-testid={`przebieg-toggle-history-${r.pojazd_id}`}
                                            >
                                                {isOpen ? <CaretDown className="w-3 h-3 text-slate-400" /> : <CaretRight className="w-3 h-3 text-slate-400" />}
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                        <span className="font-semibold">{r.numer_rejestracyjny || '—'}</span>
                                                        {r.employee_name && (
                                                            <span className="text-[10px] font-mono font-bold bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded" title="Skrót pracownika" data-testid={`przebieg-emp-skrot-${r.pojazd_id}`}>
                                                                {r.employee_name}
                                                            </span>
                                                        )}
                                                        <WlasnoscBadge value={r.wlasnosc} size="xs" />
                                                    </div>
                                                    <div className="text-xs text-slate-500">{r.marka} {r.model}</div>
                                                </div>
                                            </button>
                                        </td>
                                        <td className="py-2 px-3 text-right font-mono tabular-nums">{r.last_km ? Math.floor(r.last_km).toLocaleString('pl-PL') : '—'}</td>
                                        <td className="py-2 px-3 text-right font-mono tabular-nums text-slate-600">{r.last_oil_change_km ? Math.floor(r.last_oil_change_km).toLocaleString('pl-PL') : '—'}</td>
                                        <td className="py-2 px-3 text-right font-mono tabular-nums text-slate-600">{r.km_since_oil_change ? Math.floor(r.km_since_oil_change).toLocaleString('pl-PL') : '—'}</td>
                                        <td className="py-2 px-3 text-center">
                                            <StatusPill warning={r.warning} kmUntil={r.km_until_oil_change !== null ? Math.floor(r.km_until_oil_change) : null} />
                                        </td>
                                        <td className="py-2 px-3 text-center">
                                            {r.current_month_logged
                                                ? <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle className="w-4 h-4" weight="fill" /></span>
                                                : <span className="inline-flex items-center gap-1 text-amber-500"><Clock className="w-4 h-4" weight="fill" /></span>
                                            }
                                        </td>
                                        <td className="py-2 px-3 text-right">
                                            <button
                                                onClick={() => openDialog(r)}
                                                className="inline-flex items-center gap-1 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-semibold"
                                                data-testid={`przebieg-add-${r.pojazd_id}`}
                                            >
                                                <Plus className="w-3 h-3" /> {r.current_month_logged ? 'Popraw' : 'Wpisz'}
                                            </button>
                                        </td>
                                    </tr>
                                    {isOpen && (
                                        <tr className="bg-slate-50 border-t border-slate-100">
                                            <td colSpan={7} className="p-4">
                                                <PrzebiegiHistoryPanel data={hist} loading={historyLoading && !hist} />
                                            </td>
                                        </tr>
                                    )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {dialog && (
                <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4"
                     onClick={() => setDialog(null)}>
                    <div className="bg-white rounded-xl max-w-sm w-full" onClick={e => e.stopPropagation()} data-testid="przebieg-dialog">
                        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
                            <h3 className="font-bold">Wpisz przebieg · {dialog.numer}</h3>
                            <button onClick={() => setDialog(null)} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="p-5 space-y-3">
                            <div className="text-xs text-slate-500">
                                Okres: <span className="font-semibold text-slate-800">{MONTHS_PL[m - 1]} {y}</span>
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase font-bold text-slate-500 mb-1">Aktualny przebieg (km)</label>
                                <input
                                    type="number"
                                    value={form.km}
                                    onChange={e => setForm({ ...form, km: e.target.value })}
                                    className="w-full px-3 py-2 border border-slate-300 rounded font-mono tabular-nums text-lg"
                                    placeholder="np. 152 300"
                                    data-testid="przebieg-km-input"
                                    autoFocus
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase font-bold text-slate-500 mb-1">Notatka (opcjonalnie)</label>
                                <input
                                    type="text"
                                    value={form.notes}
                                    onChange={e => setForm({ ...form, notes: e.target.value })}
                                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
                                    data-testid="przebieg-notes-input"
                                />
                            </div>
                            {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">{err}</div>}
                        </div>
                        <div className="border-t border-slate-100 px-5 py-3 flex justify-end gap-2">
                            <button onClick={() => setDialog(null)} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">Anuluj</button>
                            <button onClick={submit} disabled={saving}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded text-sm font-semibold inline-flex items-center gap-1.5"
                                data-testid="przebieg-save">
                                {saving ? <CircleNotch className="w-4 h-4 animate-spin" /> : <FloppyDisk className="w-4 h-4" />}
                                Zapisz
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* -------- History panel: shown when a row is expanded -------- */
function PrzebiegiHistoryPanel({ data, loading }) {
    if (loading) return <div className="text-sm text-slate-500 py-4 text-center"><CircleNotch className="w-4 h-4 animate-spin inline mr-2" />Ładowanie historii…</div>;
    if (!data) return null;
    const { history = [], oil_changes = [], aggregates = {} } = data;
    if (history.length === 0) {
        return <div className="text-sm text-slate-500 text-center py-6">Brak historii pobiegów dla tego pojazdu.</div>;
    }
    const oilByYM = new Map(); // "YYYY-MM" → oil-change entry
    oil_changes.forEach(o => {
        if (o.data && o.data.length >= 7) oilByYM.set(o.data.slice(0, 7), o);
    });
    // Show newest first for scannability
    const chronoDesc = [...history].reverse();
    return (
        <div className="space-y-3" data-testid="przebiegi-history-panel">
            {/* Aggregates */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <Stat label="Wpisów" value={aggregates.months_logged || 0} />
                <Stat label="Łącznie przejechano" value={`${Math.floor(aggregates.total_km_run || 0).toLocaleString('pl-PL')} km`} />
                <Stat label="Średnio / miesiąc" value={`${Math.floor(aggregates.avg_km_per_month || 0).toLocaleString('pl-PL')} km`} />
                <Stat label="Wymian oleju" value={aggregates.oil_changes_count || 0} icon={<Drop className="w-3 h-3 text-amber-600" weight="fill" />} />
            </div>
            {/* History table */}
            <div className="border border-slate-200 rounded bg-white overflow-x-auto">
                <table className="w-full text-xs">
                    <thead className="bg-slate-100">
                        <tr>
                            <th className="text-left py-1.5 px-2 font-semibold text-slate-600">Okres</th>
                            <th className="text-right py-1.5 px-2 font-semibold text-slate-600">Przebieg</th>
                            <th className="text-right py-1.5 px-2 font-semibold text-slate-600">Δ vs poprzedni</th>
                            <th className="text-right py-1.5 px-2 font-semibold text-slate-600">Od ostatniej wymiany</th>
                            <th className="text-left py-1.5 px-2 font-semibold text-slate-600">Notatka</th>
                        </tr>
                    </thead>
                    <tbody>
                        {chronoDesc.map((h) => {
                            const ym = `${h.year}-${String(h.month).padStart(2, '0')}`;
                            const oil = oilByYM.get(ym);
                            return (
                                <tr key={`${h.year}-${h.month}`} className="border-t border-slate-100" data-testid={`history-row-${h.year}-${h.month}`}>
                                    <td className="py-1.5 px-2 font-mono">
                                        {MONTHS_PL[h.month - 1]} {h.year}
                                        {oil && (
                                            <span className="ml-1 inline-flex items-center gap-0.5 text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 text-[10px]" title={`Wymiana oleju: ${oil.przebieg} km`}>
                                                <Drop className="w-2.5 h-2.5" weight="fill" /> wymiana
                                            </span>
                                        )}
                                    </td>
                                    <td className="py-1.5 px-2 text-right font-mono tabular-nums">{Math.floor(h.km).toLocaleString('pl-PL')} km</td>
                                    <td className={`py-1.5 px-2 text-right font-mono tabular-nums ${h.delta_km == null ? "text-slate-400" : h.delta_km < 0 ? "text-rose-600" : "text-slate-700"}`}>
                                        {h.delta_km == null ? "—" : (h.delta_km >= 0 ? "+" : "") + Math.floor(h.delta_km).toLocaleString('pl-PL')}
                                    </td>
                                    <td className={`py-1.5 px-2 text-right font-mono tabular-nums ${h.km_since_oil_change == null ? "text-slate-400" : h.km_since_oil_change > 10000 ? "text-rose-600 font-semibold" : h.km_since_oil_change > 9500 ? "text-amber-700" : "text-slate-700"}`}>
                                        {h.km_since_oil_change == null ? "—" : Math.floor(h.km_since_oil_change).toLocaleString('pl-PL')}
                                    </td>
                                    <td className="py-1.5 px-2 text-slate-500 truncate max-w-[200px]" title={h.notes || ""}>{h.notes || ""}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {oil_changes.length > 0 && (
                <details className="text-xs">
                    <summary className="cursor-pointer text-slate-600 hover:text-slate-800 font-semibold">Wymiany oleju ({oil_changes.length})</summary>
                    <ul className="mt-2 pl-4 space-y-1">
                        {oil_changes.map((o, i) => (
                            <li key={i} className="text-slate-700">
                                <span className="font-mono">{o.data || "—"}</span> · przy przebiegu <span className="font-mono">{Math.floor(o.przebieg).toLocaleString('pl-PL')} km</span>
                                {o.notes && <span className="text-slate-500"> — {o.notes}</span>}
                            </li>
                        ))}
                    </ul>
                </details>
            )}
        </div>
    );
}

function Stat({ label, value, icon }) {
    return (
        <div className="bg-white border border-slate-200 rounded p-2">
            <div className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-1">{icon}{label}</div>
            <div className="text-sm font-semibold text-slate-800 mt-0.5">{value}</div>
        </div>
    );
}
